import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiAircraftModel, DigitaiAircraftStatus, DigitaiChartType, DigitaiCompany, DigitaiCreateFleet, DigitaiCreateFleetMessage, DigitaiEngineModel, DigitaiFleet, DigitaiFleetAircraft, DigitaiFleetTab, DigitaiUpdateFleetById, DigitaiUpdateFleets } from '@apps/digitai';
import { AircraftModelService } from '@apps/digitai/aircraft-model';
import { EngineModelService } from '@apps/digitai/engine-model';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/fleet';
import { FleetAircraftService } from '@apps/digitai/fleet-aircraft';
import { FleetTabService } from '@apps/digitai/fleet-tab';
import { CompanyService } from '../company';
import { GraphQLHeaders, GraphQLService, GridData, QueryStatement, parseGqlFields } from '@aurora';
import { BehaviorSubject, Observable, first, map, tap } from 'rxjs';
import { findByIdWithRelationsOperatorQuery, findByIdWithRelationsQuery, getRelations, sendMessageFleetMutation } from './fleet.graphql';
import { ChartTypeService } from '../chart-type';

@Injectable({
    providedIn: 'root',
})
export class FleetService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiFleet> | null> = new BehaviorSubject(null);
    fleetSubject$: BehaviorSubject<DigitaiFleet | null> = new BehaviorSubject(null);
    fleetsSubject$: BehaviorSubject<DigitaiFleet[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiFleet> | null>; } = {};
    fleetScoped: { [key: string]: BehaviorSubject<DigitaiFleet | null>; } = {};
    fleetsScoped: { [key: string]: BehaviorSubject<DigitaiFleet[] | null>; } = {};

    constructor(
        private readonly aircraftModelService: AircraftModelService,
        private readonly companyService: CompanyService,
        private readonly engineModelService: EngineModelService,
        private readonly fleetAircraftService: FleetAircraftService,
        private readonly fleetTabService: FleetTabService,
        private readonly graphqlService: GraphQLService,
        private readonly chartTypeService: ChartTypeService,
    ) { }

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiFleet>>
    {
        return this.paginationSubject$.asObservable();
    }

    get fleet$(): Observable<DigitaiFleet>
    {
        return this.fleetSubject$.asObservable();
    }

    get fleets$(): Observable<DigitaiFleet[]>
    {
        return this.fleetsSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiFleet>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiFleet>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeFleet(scope: string, object: DigitaiFleet): void
    {
        if (this.fleetScoped[scope])
        {
            this.fleetScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.fleetScoped[scope] = new BehaviorSubject(object);
    }

    getScopeFleet(scope: string): Observable<DigitaiFleet>
    {
        if (this.fleetScoped[scope]) return this.fleetScoped[scope].asObservable();

        this.fleetScoped[scope] = new BehaviorSubject(null);
        return this.fleetScoped[scope].asObservable();
    }

    setScopeFleets(scope: string, objects: DigitaiFleet[]): void
    {
        if (this.fleetsScoped[scope])
        {
            this.fleetsScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.fleetsScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeFleets(scope: string): Observable<DigitaiFleet[]>
    {
        if (this.fleetsScoped[scope]) return this.fleetsScoped[scope].asObservable();

        this.fleetsScoped[scope] = new BehaviorSubject(null);
        return this.fleetsScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiFleet>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiFleet>; }>({
                query: graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleet;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleet;
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleet(scope, data.object) : this.fleetSubject$.next(data.object)),
            );
    }

    findByIdWithRelations(
        {
            graphqlStatement = findByIdWithRelationsQuery,
            id = '',
            constraint = {},
            queryPaginateCompanies = {},
            constraintPaginateCompanies = {},
            queryPaginateAircraftModels = {},
            constraintPaginateAircraftModels = {},
            queryPaginateEngineModels = {},
            constraintPaginateEngineModels = {},
            queryPaginateFleetTabs = {},
            constraintPaginateFleetTabs = {},
            queryPaginateFleetAircrafts = {},
            constraintPaginateFleetAircrafts = {},
            queryGetChartTypes = {},
            constraintGetChartTypes = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            queryPaginateCompanies?: QueryStatement;
            constraintPaginateCompanies?: QueryStatement;
            queryPaginateAircraftModels?: QueryStatement;
            constraintPaginateAircraftModels?: QueryStatement;
            queryPaginateEngineModels?: QueryStatement;
            constraintPaginateEngineModels?: QueryStatement;
            queryPaginateFleetTabs?: QueryStatement;
            constraintPaginateFleetTabs?: QueryStatement;
            queryPaginateFleetAircrafts?: QueryStatement;
            constraintPaginateFleetAircrafts?: QueryStatement;
            queryGetChartTypes?: QueryStatement;
            constraintGetChartTypes?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleet;
        digitaiPaginateCompanies: GridData<DigitaiCompany>;
        digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
        digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
        digitaiPaginateFleetTabs: GridData<DigitaiFleetTab>;
        digitaiPaginateFleetAircrafts: GridData<DigitaiFleetAircraft>;
        digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
        digitaiGetChartTypes: DigitaiChartType[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleet;
                digitaiPaginateCompanies: GridData<DigitaiCompany>;
                digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
                digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
                digitaiPaginateFleetTabs: GridData<DigitaiFleetTab>;
                digitaiPaginateFleetAircrafts: GridData<DigitaiFleetAircraft>;
                digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
                digitaiGetChartTypes: DigitaiChartType[];
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryPaginateCompanies,
                    constraintPaginateCompanies,
                    queryPaginateAircraftModels,
                    constraintPaginateAircraftModels,
                    queryPaginateEngineModels,
                    constraintPaginateEngineModels,
                    queryPaginateFleetTabs,
                    constraintPaginateFleetTabs,
                    queryPaginateFleetAircrafts,
                    constraintPaginateFleetAircrafts,
                    queryGetChartTypes,
                    constraintGetChartTypes,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    if (scope)
                    {
                        this.setScopeFleet(scope, data.object);
                    }
                    else
                    {
                        this.fleetSubject$.next(data.object);
                    }
                    this.companyService.paginationSubject$.next(data.digitaiPaginateCompanies);
                    this.aircraftModelService.paginationSubject$.next(data.digitaiPaginateAircraftModels);
                    this.engineModelService.paginationSubject$.next(data.digitaiPaginateEngineModels);
                    this.fleetTabService.paginationSubject$.next(data.digitaiPaginateFleetTabs);
                    this.fleetAircraftService.paginationSubject$.next(data.digitaiPaginateFleetAircrafts);
                    this.chartTypeService.chartTypesSubject$.next(data.digitaiGetChartTypes);
                }),
            );
    }

    findByIdWithRelationsOperator(
        {
            graphqlStatement = findByIdWithRelationsOperatorQuery,
            id = '',
            constraint = {},
            queryPaginateAircraftModels = {},
            constraintPaginateAircraftModels = {},
            queryPaginateEngineModels = {},
            constraintPaginateEngineModels = {},
            queryPaginateFleetTabs = {},
            constraintPaginateFleetTabs = {},
            queryPaginateFleetAircrafts = {},
            constraintPaginateFleetAircrafts = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            queryPaginateAircraftModels?: QueryStatement;
            constraintPaginateAircraftModels?: QueryStatement;
            queryPaginateEngineModels?: QueryStatement;
            constraintPaginateEngineModels?: QueryStatement;
            queryPaginateFleetTabs?: QueryStatement;
            constraintPaginateFleetTabs?: QueryStatement;
            queryPaginateFleetAircrafts?: QueryStatement;
            constraintPaginateFleetAircrafts?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleet;
        digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
        digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
        digitaiPaginateFleetTabs: GridData<DigitaiFleetTab>;
        digitaiPaginateFleetAircrafts: GridData<DigitaiFleetAircraft>;
        digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleet;
                digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
                digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
                digitaiPaginateFleetTabs: GridData<DigitaiFleetTab>;
                digitaiPaginateFleetAircrafts: GridData<DigitaiFleetAircraft>;
                digitaiGetAircraftStatuses: DigitaiAircraftStatus[];
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryPaginateAircraftModels,
                    constraintPaginateAircraftModels,
                    queryPaginateEngineModels,
                    constraintPaginateEngineModels,
                    queryPaginateFleetTabs,
                    constraintPaginateFleetTabs,
                    queryPaginateFleetAircrafts,
                    constraintPaginateFleetAircrafts,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    if (scope)
                    {
                        this.setScopeFleet(scope, data.object);
                    }
                    else
                    {
                        this.fleetSubject$.next(data.object);
                    }
                    this.aircraftModelService.paginationSubject$.next(data.digitaiPaginateAircraftModels);
                    this.engineModelService.paginationSubject$.next(data.digitaiPaginateEngineModels);
                    this.fleetTabService.paginationSubject$.next(data.digitaiPaginateFleetTabs);
                    this.fleetAircraftService.paginationSubject$.next(data.digitaiPaginateFleetAircrafts);
                }),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleet;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleet;
            }>({
                query: parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleet(scope, data.object) : this.fleetSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiFleet[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiFleet[];
            }>({
                query: parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleets(scope, data.objects) : this.fleetsSubject$.next(data.objects)),
            );
    }

    getRelations(
        {
            queryPaginateCompanies = {},
            constraintPaginateCompanies = {},
            queryPaginateAircraftModels = {},
            constraintPaginateAircraftModels = {},
            queryPaginateEngineModels = {},
            constraintPaginateEngineModels = {},
            queryGetChartTypes = {},
            constraintGetChartTypes = {},
            headers = {},
        }: {
            queryPaginateCompanies?: QueryStatement;
            constraintPaginateCompanies?: QueryStatement;
            queryPaginateAircraftModels?: QueryStatement;
            constraintPaginateAircraftModels?: QueryStatement;
            queryPaginateEngineModels?: QueryStatement;
            constraintPaginateEngineModels?: QueryStatement;
            queryGetChartTypes?: QueryStatement;
            constraintGetChartTypes?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<{
        digitaiPaginateCompanies: GridData<DigitaiCompany>;
        digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
        digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
        digitaiGetChartTypes: DigitaiChartType[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                digitaiPaginateCompanies: GridData<DigitaiCompany>;
                digitaiPaginateAircraftModels: GridData<DigitaiAircraftModel>;
                digitaiPaginateEngineModels: GridData<DigitaiEngineModel>;
                digitaiGetChartTypes: DigitaiChartType[];
            }>({
                query: getRelations,
                variables: {
                    queryPaginateCompanies,
                    constraintPaginateCompanies,
                    queryPaginateAircraftModels,
                    constraintPaginateAircraftModels,
                    queryPaginateEngineModels,
                    constraintPaginateEngineModels,
                    queryGetChartTypes,
                    constraintGetChartTypes,

                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    this.companyService.paginationSubject$.next(data.digitaiPaginateCompanies);
                    this.aircraftModelService.paginationSubject$.next(data.digitaiPaginateAircraftModels);
                    this.engineModelService.paginationSubject$.next(data.digitaiPaginateEngineModels);
                    this.chartTypeService.chartTypesSubject$.next(data.digitaiGetChartTypes);
                }),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateFleet;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateFleet[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateFleetById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateFleets;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    // Mutation additionalApis
    sendMessageFleet<T>(
        {
            graphqlStatement = sendMessageFleetMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateFleetMessage;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation: graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }


}
